import React from 'react';
import { AlertTriangle, FileWarning, ExternalLink, Copyright, Scale, Info } from 'lucide-react';

const DisclaimerPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-amber-50">
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <div className="text-center mb-12">
            <AlertTriangle className="w-16 h-16 text-amber-600 mx-auto mb-4" />
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Disclaimer Policy</h1>
            <p className="text-gray-600 text-lg">Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 text-lg leading-relaxed mb-8">
              This Disclaimer Policy outlines important information about the use of the Rushmore Publishing website and services. By accessing or using our website, you acknowledge and agree to the terms set forth in this disclaimer.
            </p>

            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <Info className="w-6 h-6 text-amber-600" />
                <h2 className="text-2xl font-bold text-gray-900 m-0">General Information</h2>
              </div>
              <p className="text-gray-700 leading-relaxed">
                The information provided on the Rushmore Publishing website is intended for general informational purposes only. While we make every effort to ensure the accuracy, completeness, and timeliness of the information presented, we make no representations or warranties of any kind, express or implied, about the completeness, accuracy, reliability, suitability, or availability of the website or the information, products, services, or related graphics contained on the website.
              </p>
            </div>

            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <FileWarning className="w-6 h-6 text-amber-600" />
                <h2 className="text-2xl font-bold text-gray-900 m-0">No Guarantees of Results</h2>
              </div>
              <p className="text-gray-700 leading-relaxed mb-4">
                While we are committed to delivering high-quality publishing services, we want to be clear about what we can and cannot guarantee:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Publishing Success:</strong> We cannot guarantee that your book will become a bestseller, achieve specific sales targets, or receive particular reviews</li>
                <li><strong>Timeline Variances:</strong> Project timelines are estimates and may vary based on complexity, revisions, and external factors</li>
                <li><strong>Market Response:</strong> Reader reception, market trends, and commercial success depend on many factors beyond our control</li>
                <li><strong>Platform Acceptance:</strong> While we format books to meet platform requirements, final acceptance by retailers or distributors is subject to their policies</li>
                <li><strong>Individual Results:</strong> Every author's experience and outcomes will differ based on numerous variables</li>
              </ul>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Services and Portfolio Examples</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Examples of our work, portfolio pieces, and service descriptions shown on our website are provided for illustrative purposes only. These examples:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>May not reflect the exact deliverables you will receive</li>
                <li>Represent a range of styles and approaches we can offer</li>
                <li>Are customized to each client's unique needs and specifications</li>
                <li>May have been created under different circumstances or requirements than your project</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                All services are tailored to individual client requirements, and actual deliverables will be determined during the project consultation and agreement phase.
              </p>
            </div>

            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <ExternalLink className="w-6 h-6 text-amber-600" />
                <h2 className="text-2xl font-bold text-gray-900 m-0">Third-Party Links and Content</h2>
              </div>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our website may contain links to third-party websites, resources, or services that are not owned, controlled, or maintained by Rushmore Publishing. These links are provided solely for convenience and informational purposes.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Please note:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>We do not endorse, approve, or guarantee the accuracy of information on external sites</li>
                <li>We are not responsible for the content, privacy practices, or terms of third-party websites</li>
                <li>We assume no liability for any damages or losses arising from your use of external links</li>
                <li>Accessing third-party websites is at your own risk</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                We encourage you to review the terms and privacy policies of any third-party sites you visit.
              </p>
            </div>

            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <Copyright className="w-6 h-6 text-amber-600" />
                <h2 className="text-2xl font-bold text-gray-900 m-0">Intellectual Property Rights</h2>
              </div>
              <p className="text-gray-700 leading-relaxed mb-4">
                All content on the Rushmore Publishing website, including but not limited to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Text, graphics, logos, and images</li>
                <li>Videos, audio files, and multimedia content</li>
                <li>Software, code, and design elements</li>
                <li>Trademarks, service marks, and trade names</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                ...is protected by copyright, trademark, and other intellectual property laws. Unauthorized use, reproduction, distribution, or modification of any content from this website without express written permission from Rushmore Publishing is strictly prohibited and may result in legal action.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Trademark Notice</h2>
              <p className="text-gray-700 leading-relaxed">
                Rushmore Publishing and our logo are trademarks of our company. Any third-party trademarks, logos, or brand names displayed on our website are the property of their respective owners and are used for identification and reference purposes only. Their inclusion does not imply endorsement, affiliation, or sponsorship by or of these companies.
              </p>
            </div>

            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <Scale className="w-6 h-6 text-amber-600" />
                <h2 className="text-2xl font-bold text-gray-900 m-0">Limitation of Liability</h2>
              </div>
              <p className="text-gray-700 leading-relaxed mb-4">
                To the fullest extent permitted by applicable law, Rushmore Publishing shall not be liable for:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Any direct, indirect, incidental, consequential, or punitive damages</li>
                <li>Loss of profits, revenue, data, or business opportunities</li>
                <li>Errors, omissions, or inaccuracies in website content</li>
                <li>Service interruptions, delays, or technical failures</li>
                <li>Unauthorized access to or alteration of your data</li>
                <li>Actions or content of third parties linked from our website</li>
                <li>Any damages arising from your reliance on information provided on our website</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                Your use of our website and services is at your sole risk. Our services are provided on an "as is" and "as available" basis without warranties of any kind.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Professional Advice Disclaimer</h2>
              <p className="text-gray-700 leading-relaxed">
                The information and content provided on our website is not intended to constitute professional advice, whether legal, financial, tax, or otherwise. While we share industry insights and publishing guidance, you should not rely solely on this information for making important decisions. We recommend consulting with qualified professionals-such as attorneys, accountants, or literary agents-for advice specific to your situation.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Content Accuracy and Updates</h2>
              <p className="text-gray-700 leading-relaxed">
                While we strive to keep all information on our website current and accurate, the publishing industry evolves rapidly. Pricing, service offerings, processes, and other details may change without prior notice. We reserve the right to modify, update, or remove any content at our discretion. The information provided should not be considered a binding offer or quote until confirmed in writing.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Technical Issues</h2>
              <p className="text-gray-700 leading-relaxed">
                We make reasonable efforts to maintain our website's functionality and security. However, we cannot guarantee uninterrupted or error-free operation. Technical issues, maintenance, or circumstances beyond our control may occasionally affect website availability or performance. We are not responsible for any inconvenience or losses resulting from such issues.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">User-Generated Content</h2>
              <p className="text-gray-700 leading-relaxed">
                If our website allows user comments, reviews, or other submissions, please note that these reflect the views of individual users and not necessarily those of Rushmore Publishing. We do not endorse or verify user-generated content and are not responsible for its accuracy or appropriateness.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Jurisdictional Issues</h2>
              <p className="text-gray-700 leading-relaxed">
                This website is operated from the United States. If you access our website from other locations, you are responsible for compliance with local laws. We make no representation that materials on our website are appropriate or available for use in all locations.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to This Disclaimer</h2>
              <p className="text-gray-700 leading-relaxed">
                Rushmore Publishing reserves the right to modify, update, or revise this Disclaimer Policy at any time without prior notice. Any changes will be effective immediately upon posting to this page, with the updated date reflected at the top of the document. Your continued use of our website after changes are posted constitutes acceptance of the modified disclaimer.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Severability</h2>
              <p className="text-gray-700 leading-relaxed">
                If any provision of this Disclaimer Policy is found to be invalid, illegal, or unenforceable by a court of competent jurisdiction, such provision shall be modified to the minimum extent necessary to make it valid and enforceable. The remaining provisions shall continue in full force and effect.
              </p>
            </div>

            <div className="bg-amber-50 border-l-4 border-amber-600 p-6 rounded-r-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Questions or Concerns</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have questions or concerns about this Disclaimer Policy, please contact us:
              </p>
              <div className="text-gray-700 space-y-2">
                <p><strong>Rushmore Publishing</strong></p>
                <p>Email: info@rushmorepublishing.com</p>
                <p>Phone: +1 (866) 978-8498</p>
              </div>
            </div>

            <div className="mt-8 p-4 bg-gray-100 rounded-lg">
              <p className="text-sm text-gray-600 leading-relaxed">
                <strong>Acknowledgment:</strong> By using the Rushmore Publishing website, you acknowledge that you have read, understood, and agree to be bound by this Disclaimer Policy. If you do not agree with these terms, please discontinue use of our website immediately.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DisclaimerPolicy;